import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/ui/toast';
import { setServerErrorReporter, type ServerErrorContext } from '@/lib/api-fetch';
import { openFeedbackModal } from './feedback-store';

const REPORT_RATE_LIMIT_MS = 30_000;

/**
 * Bridges 5xx responses → "Something went wrong — Report this?" toast.
 *
 * Mounted once in the AppShell; registers a global server-error reporter
 * (via `setServerErrorReporter`) that fires a sonner toast with an action
 * button. Clicking the action opens the FeedbackModal pre-filled with the
 * error context + (when available) a Sentry event id captured at the
 * boundary.
 *
 * Per the spec the toast is rate-limited per error fingerprint to one per
 * {@link REPORT_RATE_LIMIT_MS}.
 */
export function ServerErrorBridge(): null {
  const { t } = useTranslation();
  const lastSeenRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const unregister = setServerErrorReporter((ctx: ServerErrorContext) => {
      const fingerprint = `${ctx.method} ${ctx.url} ${ctx.status}`;
      const now = Date.now();
      const last = lastSeenRef.current.get(fingerprint) ?? 0;
      if (now - last < REPORT_RATE_LIMIT_MS) return;
      lastSeenRef.current.set(fingerprint, now);

      // Capture a Sentry event id when the SDK is loaded; we read it
      // through the global window hook the FE Sentry init publishes if
      // any. The FE may not have a DSN, in which case eventId stays null.
      const sentryEventId =
        (window as { __SENVIRO_LAST_SENTRY_EVENT_ID__?: string })
          .__SENVIRO_LAST_SENTRY_EVENT_ID__ ?? null;

      toast.error(t('feedback.serverError.title'), {
        description: t('feedback.serverError.description'),
        duration: 8000,
        action: {
          label: t('feedback.serverError.report'),
          onClick: () => {
            openFeedbackModal({
              subject: t('feedback.serverError.subjectFromContext', {
                method: ctx.method,
                url: ctx.url,
                status: ctx.status,
              }),
              description: t('feedback.serverError.descriptionFromContext', {
                message: ctx.message,
              }),
              severity: 'medium',
              sentryEventId,
            });
          },
        },
      });
    });
    return () => {
      unregister();
    };
  }, [t]);

  return null;
}
